import { Badge, Card, Group, Stack, Title } from '@mantine/core';
import type { CandidateEntry } from '../api/types';
import { InfoRow } from './InfoRow';

interface CandidateInfoCardProps {
    candidate: CandidateEntry;
}

/**
 * Read-only display of a candidate row, split into vendor-identity and
 * product-identity sections. Mirrors VendorInfoCard so the Candidate Catalog
 * detail and the Company Catalog detail read the same way. Reviewers don't edit
 * candidates in v1 (see promote endpoint — the reviewer-supplied body is the
 * authoritative input for the new vendor row).
 */
export const CandidateInfoCard = ({ candidate }: CandidateInfoCardProps): React.JSX.Element => {
    return (
        <Card withBorder radius="md" padding="lg" data-testid="CandidateInfoCard">
            <Group justify="space-between" mb="md">
                <Title order={4}>Candidate Vendor Info</Title>
                {candidate.is_default ? (
                    <Badge variant="light" color="blue">
                        Default product
                    </Badge>
                ) : null}
            </Group>
            <Stack gap="xs">
                <InfoRow label="Name" value={candidate.name} />
                <InfoRow label="Domain" value={candidate.domain} />
                <InfoRow label="Website" value={candidate.website_url} isLink />
                <InfoRow label="Trust Center" value={candidate.trust_center_url} isLink />
                <InfoRow label="Open Trust" value={candidate.open_trust_url} isLink />
                <InfoRow label="Logo" value={candidate.logo} isLink />
                <InfoRow label="Favicon" value={candidate.favicon} isLink />
                <InfoRow label="Provider" value={candidate.provider_abbreviation} />
                <InfoRow label="Organization ID" value={candidate.organization_id} />
            </Stack>

            <Title order={4} mt="lg" mb="md">
                Product Info
            </Title>
            <Stack gap="xs">
                <InfoRow label="Product Name" value={candidate.product_name} />
                <InfoRow label="Slug" value={candidate.slug} />
                <InfoRow label="Is Default" value={candidate.is_default ? 'Yes' : 'No'} />
                <InfoRow label="Candidate ID" value={String(candidate.id)} />
                <InfoRow label="Metadata" value={candidate.metadata} />
                <InfoRow label="Tenants" value={String(candidate.tenant_count)} />
            </Stack>
        </Card>
    );
};
